import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import axios from "axios";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Switch from "@mui/material/Switch";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import { useAuth } from "layouts/authentication/auth/auth";
import API_BASE_URL from "config/api";
import { PUBLIC_BRAND_BACKGROUND, PUBLIC_COLORS } from "assets/theme/publicTokens";

// Varsayılan marka metinleri. `Basic` bir de emlak ofisi girişinde
// (`/emlak-ofisi-girisi`) kullanılıyor; oradaki tek fark METİNLER, kimlik
// doğrulama akışı aynı. İkinci bir SignIn kopyası çıkarmak login mantığını
// iki yerde bakım gerektiren hale sokardı.
const VARSAYILAN_MARKA = {
  markaBaslik: "Mülk yönetimi,\nyeniden tasarlandı.",
  markaAciklama: "Mülklerinizi, kiracılarınızı ve arızalarınızı tek yerden kolayca yönetin.",
  markaMaddeler: [
    { label: "Akıllı mülk takibi", icon: "🏢" },
    { label: "Anlık arıza bildirimi", icon: "🔧" },
    { label: "Güvenli ödeme sistemi", icon: "🔒" },
  ],
  baslik: "Hoş geldiniz",
  altBaslik: "Hesabınıza giriş yapın",
  altBilgiMetni: "Hesabınız yok mu?",
  altBilgiLinkMetni: "Bize Ulaşın",
  altBilgiLinkYolu: "/sizi-arayalim",
};

function Basic({ marka }) {
  const m = { ...VARSAYILAN_MARKA, ...(marka || {}) };
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ username: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSetRememberMe = () => setRememberMe(!rememberMe);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const response = await axios.post(
        `${API_BASE_URL}/auth/login`,
        { username: formData.username, password: formData.password, remember_me: rememberMe },
        { withCredentials: true, headers: { "Content-Type": "application/json" } }
      );
      if (response.data && response.data.user) {
        const userData = response.data.user;
        login(userData);

        // Honor an intended destination set before signup/login (e.g. Teknik Hizmet flow).
        // localStorage (not sessionStorage) so it survives the signup → email verify → sign-in
        // detour even if the user switches/reopens the tab.
        const pendingRedirect =
          localStorage.getItem("postLoginRedirect") || sessionStorage.getItem("postLoginRedirect");
        if (pendingRedirect) {
          localStorage.removeItem("postLoginRedirect");
          sessionStorage.removeItem("postLoginRedirect");
          navigate(pendingRedirect);
          return;
        }

        if (
          userData.uyelik &&
          !userData.admin &&
          !userData.kiraci &&
          !userData.usta &&
          !userData.ev_sahibi
        ) {
          navigate("/uyelik/ilan");
        } else if (
          userData.emlakci &&
          !userData.admin &&
          !userData.kiraci &&
          !userData.usta &&
          !userData.ev_sahibi
        ) {
          navigate("/emlakci/ilan");
        } else {
          navigate("/dashboard");
        }
      }
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          err.response?.data?.message ||
          "Giriş başarısız. Lütfen bilgilerinizi kontrol edin."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {/* ── Left brand panel ── */}
      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          justifyContent: "space-between",
          width: { md: "42%", lg: "45%" },
          background: PUBLIC_BRAND_BACKGROUND,
          position: "relative",
          overflow: "hidden",
          p: { md: 5, lg: 7 },
        }}
      >
        {/* Decorative blobs */}
        <Box
          sx={{
            position: "absolute",
            width: 520,
            height: 520,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.04)",
            top: -220,
            right: -200,
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            width: 320,
            height: 320,
            borderRadius: "50%",
            background: "rgba(245,98,66,0.12)",
            bottom: 60,
            left: -120,
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            width: 140,
            height: 140,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.07)",
            bottom: 320,
            right: -40,
            pointerEvents: "none",
          }}
        />

        {/* Logo row */}
        <Box sx={{ display: "flex", alignItems: "center", zIndex: 1 }}>
          <Box
            component="img"
            src={require("assets/images/renix-logo-white-transparent.png")}
            alt="Renix"
            sx={{
              width: 72,
              height: "auto",
            }}
          />
        </Box>

        {/* Center content */}
        <Box sx={{ zIndex: 1 }}>
          <Typography
            sx={{
              color: "#ffffff",
              fontWeight: 800,
              fontSize: { md: "2rem", lg: "2.5rem" },
              lineHeight: 1.2,
              letterSpacing: "-1.5px",
              mb: 2.5,
              whiteSpace: "pre-line",
            }}
          >
            {m.markaBaslik}
          </Typography>
          <Typography
            sx={{
              color: "rgba(255,255,255,0.6)",
              lineHeight: 1.8,
              maxWidth: 300,
              fontSize: "0.95rem",
            }}
          >
            {m.markaAciklama}
          </Typography>

          <Box sx={{ mt: 5, display: "flex", flexDirection: "column", gap: 2.5 }}>
            {m.markaMaddeler.map((item) => (
              <Box key={item.label} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 1.5,
                    background: "rgba(255,255,255,0.1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "1rem",
                    flexShrink: 0,
                  }}
                >
                  {item.icon}
                </Box>
                <Typography
                  sx={{ color: "rgba(255,255,255,0.82)", fontWeight: 500, fontSize: "0.9rem" }}
                >
                  {item.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Footer */}
        <Typography sx={{ color: "rgba(255,255,255,0.3)", fontSize: "0.75rem", zIndex: 1 }}>
          © 2026 Renix Mülk Yönetimi
        </Typography>
      </Box>

      {/* ── Right form panel ── */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          bgcolor: PUBLIC_COLORS.canvas,
          px: { xs: 3, sm: 6, md: 7, lg: 10 },
          py: 6,
          minHeight: "100vh",
        }}
      >
        {/* Mobile logo */}
        <Box
          sx={{
            display: { xs: "flex", md: "none" },
            alignItems: "center",
            mb: 5,
            alignSelf: "flex-start",
          }}
        >
          <Box
            component="img"
            src={require("assets/images/renix-logo-512x512.png")}
            alt="Renix"
            sx={{ width: 64 }}
          />
        </Box>

        <Box sx={{ width: "100%", maxWidth: 380 }}>
          <Typography
            sx={{
              fontWeight: 800,
              color: PUBLIC_COLORS.ink,
              fontSize: "1.75rem",
              letterSpacing: "-0.5px",
              mb: 0.75,
            }}
          >
            {m.baslik}
          </Typography>
          <Typography sx={{ color: PUBLIC_COLORS.muted, mb: 4, fontSize: "0.95rem" }}>
            {m.altBaslik}
          </Typography>

          {error && (
            <Box
              sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: "#fef2f2", border: "1px solid #fecaca" }}
            >
              <Typography sx={{ color: "#dc2626", fontSize: "0.875rem" }}>{error}</Typography>
            </Box>
          )}

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}
          >
            {/* autocomplete tokens: WCAG 2.2 AA 1.3.5 (Identify Input Purpose) and they let
                password managers fill the form. */}
            <MDInput
              type="text"
              label="Kullanıcı Adı"
              name="username"
              autoComplete="username"
              value={formData.username}
              onChange={handleInputChange}
              fullWidth
              required
            />
            <MDInput
              type={showPassword ? "text" : "password"}
              label="Şifre"
              name="password"
              autoComplete="current-password"
              value={formData.password}
              onChange={handleInputChange}
              fullWidth
              required
            />

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={showPassword}
                      onChange={(event) => setShowPassword(event.target.checked)}
                    />
                  }
                  label="Şifreyi göster"
                  sx={{ m: 0, "& .MuiFormControlLabel-label": { fontSize: "0.875rem" } }}
                />
                <Switch
                  checked={rememberMe}
                  onChange={handleSetRememberMe}
                  size="small"
                  color="info"
                />
                <Typography
                  sx={{
                    color: PUBLIC_COLORS.muted,
                    cursor: "pointer",
                    userSelect: "none",
                    fontSize: "0.875rem",
                  }}
                  onClick={handleSetRememberMe}
                >
                  Beni hatırla
                </Typography>
              </Box>
              <Link to="/authentication/forgot-password" style={{ textDecoration: "none" }}>
                <Typography
                  sx={{ color: PUBLIC_COLORS.blue, fontWeight: 600, fontSize: "0.875rem" }}
                >
                  Şifremi Unuttum
                </Typography>
              </Link>
            </Box>

            <MDButton
              type="submit"
              variant="contained"
              color="info"
              fullWidth
              disabled={loading}
              sx={{ mt: 0.5, py: 1.5, fontSize: "0.9rem", letterSpacing: "0.3px" }}
            >
              {loading ? "Giriş Yapılıyor..." : "Giriş Yap"}
            </MDButton>
          </Box>

          <Box
            sx={{
              mt: 4,
              pt: 3,
              borderTop: `1px solid ${PUBLIC_COLORS.border}`,
              textAlign: "center",
            }}
          >
            <Typography sx={{ color: PUBLIC_COLORS.muted, fontSize: "0.875rem" }}>
              {m.altBilgiMetni}{" "}
              <Link to={m.altBilgiLinkYolu} style={{ textDecoration: "none" }}>
                <Box component="span" sx={{ color: PUBLIC_COLORS.blue, fontWeight: 600 }}>
                  {m.altBilgiLinkMetni}
                </Box>
              </Link>
            </Typography>
            <Link to="/" style={{ textDecoration: "none" }}>
              <Typography
                sx={{
                  color: PUBLIC_COLORS.muted,
                  mt: 1.5,
                  display: "block",
                  fontSize: "0.875rem",
                }}
              >
                ← Anasayfaya Dön
              </Typography>
            </Link>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

Basic.propTypes = {
  // Yalniz metinler; kimlik dogrulama davranisi propla degismez.
  marka: PropTypes.shape({
    markaBaslik: PropTypes.string,
    markaAciklama: PropTypes.string,
    markaMaddeler: PropTypes.arrayOf(
      PropTypes.shape({ label: PropTypes.string, icon: PropTypes.string })
    ),
    baslik: PropTypes.string,
    altBaslik: PropTypes.string,
    altBilgiMetni: PropTypes.string,
    altBilgiLinkMetni: PropTypes.string,
    altBilgiLinkYolu: PropTypes.string,
  }),
};

Basic.defaultProps = {
  marka: null,
};

export default Basic;
